import * as React from "react";
import { useState, useEffect } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Box, CircularProgress } from "@mui/material";
import { useChats } from "../../hooks/useChats";
import { useFolders } from "../../hooks/useFolders";
import { RenameDialog } from "../common/RenameDialog";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { TreeNode } from "./TreeNode";
import { convertFolderToNode } from "./treeUtils";
import { useFolderTreeHandlers } from "./useFolderTreeHandlers";
import type { FolderNode } from "./types";
import type { Folder } from "../../services/api";
import type { FoldersTreeViewProps } from "./types";

export const FoldersTreeView: React.FC<FoldersTreeViewProps> = ({
  onItemsChange,
  selectedId,
  onSelect,
}) => {
  const [folders, setFolders] = useState<FolderNode<Folder>[]>([]);
  const [conversationsToday, setConversationsToday] = useState<FolderNode[]>(
    []
  );
  const [conversationsPast, setConversationsPast] = useState<FolderNode[]>([]);
  const [renameDialog, setRenameDialog] = useState<{
    open: boolean;
    mode: "rename" | "create";
    targetType: "folder" | "chat";
    id: string;
    currentName: string;
    parentId?: string;
  }>({
    open: false,
    mode: "rename",
    targetType: "folder",
    id: "",
    currentName: "",
  });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    node: FolderNode | null;
  }>({ open: false, node: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { data: chats, isLoading: isLoadingChats } = useChats();
  const { data: foldersData } = useFolders();

  const {
    handleMove,
    handleRenameClick,
    handleRenameConfirm,
    handleDelete,
    handleCreateFolder,
  } = useFolderTreeHandlers(
    folders,
    conversationsToday,
    conversationsPast,
    chats,
    renameDialog,
    setRenameDialog
  );

  useEffect(() => {
    if (!foldersData) return;
    const folderNodes = foldersData
      .filter((f) => f.type === 0)
      .map(convertFolderToNode);
    setFolders(folderNodes);
  }, [foldersData]);

  useEffect(() => {
    if (!chats) return;

    const now = new Date();
    const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const todayChats: FolderNode[] = [];
    const pastChats: FolderNode[] = [];

    chats.forEach((chat) => {
      const utcStr = chat.update_date.endsWith("Z") ? chat.update_date : `${chat.update_date}Z`;
      const updateDate = new Date(utcStr);
      const updateLocalDate = new Date(
        updateDate.getFullYear(),
        updateDate.getMonth(),
        updateDate.getDate()
      );

      const chatNode: FolderNode = {
        id: chat.id,
        label: chat.title || "Nuova chat",
        type: "conversation",
      };

      if (updateLocalDate.getTime() === todayLocal.getTime()) {
        todayChats.push(chatNode);
      } else {
        pastChats.push(chatNode);
      }
    });

    todayChats.sort((a, b) => {
      const chatA = chats.find((c) => c.id === a.id);
      const chatB = chats.find((c) => c.id === b.id);
      if (!chatA || !chatB) return 0;
      return (
        new Date(chatB.update_date).getTime() -
        new Date(chatA.update_date).getTime()
      );
    });

    pastChats.sort((a, b) => {
      const chatA = chats.find((c) => c.id === a.id);
      const chatB = chats.find((c) => c.id === b.id);
      if (!chatA || !chatB) return 0;
      return (
        new Date(chatB.update_date).getTime() -
        new Date(chatA.update_date).getTime()
      );
    });

    setConversationsToday(todayChats);
    setConversationsPast(pastChats);
  }, [chats]);

  const foldersRoot: FolderNode<Folder> = {
    id: "root-folders",
    label: "Cartelle",
    type: "folder",
    children: folders,
  };

  const conversationsRoot: FolderNode = {
    id: "root-conversations",
    label: "Conversazioni",
    type: "folder",
    children: [
      {
        id: "today-section",
        label: "Oggi",
        type: "folder",
        children: conversationsToday,
      },
      {
        id: "past-section",
        label: "Passate",
        type: "folder",
        children: conversationsPast,
      },
    ],
  };

  if (isLoadingChats) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 4,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ width: "100%", px: 0.6 }} onClick={(e) => e.stopPropagation()}>
        <TreeNode
          node={foldersRoot}
          onMove={handleMove}
          onRename={handleRenameClick}
          onDelete={(node) => setDeleteDialog({ open: true, node })}
          onCreateFolder={handleCreateFolder}
          isRoot={true}
          selectedId={selectedId}
          onSelect={(node) => {
            const nodeData = (node as FolderNode<Folder>).data as
              | Folder
              | undefined;
            const chatId = nodeData?.chat_id ?? node.id;
            if (chatId && onSelect) onSelect(chatId);
          }}
        />
        <Box sx={{ mt: 4 }} />
        <TreeNode
          node={conversationsRoot}
          onMove={handleMove}
          onRename={handleRenameClick}
          onDelete={(node) => setDeleteDialog({ open: true, node })}
          onCreateFolder={handleCreateFolder}
          isRoot={true}
          selectedId={selectedId}
          onSelect={(node) => onSelect?.(node.id)}
        />
      </Box>
      <RenameDialog
        open={renameDialog.open}
        title={renameDialog.mode === "create" ? "Nuova cartella" : "Rinomina"}
        label="Nome"
        currentName={renameDialog.currentName}
        onClose={() =>
          setRenameDialog({
            open: false,
            mode: "rename",
            targetType: "folder",
            id: "",
            currentName: "",
          })
        }
        onConfirm={handleRenameConfirm}
      />
      <ConfirmDialog
        open={deleteDialog.open}
        title="Conferma eliminazione"
        content={
          deleteDialog.node
            ? `Sei sicuro di voler eliminare "${deleteDialog.node.label}"?`
            : "Sei sicuro di voler procedere?"
        }
        confirmText="Elimina"
        cancelText="Annulla"
        loading={deleteLoading}
        onCancel={() => setDeleteDialog({ open: false, node: null })}
        onConfirm={async () => {
          if (!deleteDialog.node) return;
          setDeleteLoading(true);
          try {
            await handleDelete(deleteDialog.node);
            setDeleteDialog({ open: false, node: null });
          } finally {
            setDeleteLoading(false);
          }
        }}
      />
    </DndProvider>
  );
};
